import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";

// Layout
import Navbar from "./components/layout/Navbar";

// Auth & Advisor Pages
import LoginPage from "./pages/LoginPage";
import OnboardingPage from "./pages/OnboardingPage";
import DashboardPage from "./pages/DashboardPage";
import SettingsPage from "./pages/SettingsPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";

// Backtest Pages (from Backtestmvp)
import PortfolioDashboard from "./components/portfolio/PortfolioDashboard";
import Backtesting from "./components/backtest/Backtesting";
import Screening from "./components/screening/Screening";
import GeographicView from "./components/map/GeographicView";

import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        try {
          const response = await axios.post(`${API}/auth/session`, {
            session_id: sessionId
          });
          
          window.history.replaceState(null, "", window.location.pathname);
          
          if (response.data.profile) {
            navigate("/advisor", { state: { user: response.data } });
          } else {
            navigate("/onboarding", { state: { user: response.data } });
          }
        } catch (error) {
          console.error("Auth error:", error);
          navigate("/login");
        }
      } else {
        navigate("/login");
      }
    };

    processAuth();
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Connexion en cours...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requireProfile = true }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
        
        if (requireProfile && !response.data.profile && location.pathname !== "/onboarding") {
          navigate("/onboarding");
        }
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/login");
      }
    };

    checkAuth();
  }, [location, navigate, requireProfile]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    navigate("/login");
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children({ user, setUser, handleLogout });
};

// Layout with Navbar
const AppLayout = ({ children, user, onLogout }) => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} onLogout={onLogout} />
      <main className="max-w-7xl mx-auto px-4 py-6">
        {children}
      </main>
    </div>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      
      {/* Onboarding (no navbar) */}
      <Route 
        path="/onboarding" 
        element={
          <ProtectedRoute requireProfile={false}>
            {({ user, setUser }) => <OnboardingPage user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      
      {/* Advisor routes */}
      <Route 
        path="/advisor" 
        element={
          <ProtectedRoute>
            {({ user, setUser, handleLogout }) => (
              <AppLayout user={user} onLogout={handleLogout}>
                <DashboardPage user={user} setUser={setUser} hideNav={true} />
              </AppLayout>
            )}
          </ProtectedRoute>
        } 
      />
      
      {/* Portfolio routes (from Backtestmvp) */}
      <Route 
        path="/portfolio" 
        element={
          <ProtectedRoute>
            {({ user, handleLogout }) => (
              <AppLayout user={user} onLogout={handleLogout}>
                <PortfolioDashboard />
              </AppLayout>
            )}
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/backtest" 
        element={
          <ProtectedRoute>
            {({ user, handleLogout }) => (
              <AppLayout user={user} onLogout={handleLogout}>
                <Backtesting />
              </AppLayout>
            )}
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/screening" 
        element={
          <ProtectedRoute>
            {({ user, handleLogout }) => (
              <AppLayout user={user} onLogout={handleLogout}>
                <Screening />
              </AppLayout>
            )}
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/geographic" 
        element={
          <ProtectedRoute>
            {({ user, handleLogout }) => (
              <AppLayout user={user} onLogout={handleLogout}>
                <GeographicView />
              </AppLayout>
            )}
          </ProtectedRoute>
        } 
      />
      
      {/* Settings */}
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <SettingsPage user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      
      {/* Payment */}
      <Route 
        path="/payment/success" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <PaymentSuccessPage user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      
      {/* Redirects */}
      <Route path="/" element={<Navigate to="/advisor" replace />} />
      <Route path="/dashboard" element={<Navigate to="/advisor" replace />} />
      <Route path="*" element={<Navigate to="/advisor" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background noise-bg">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
